/**
 * Small UI helper for the admin post list:
 *   • Shows nothing for `latest` posts (no expiry rule applies).
 *   • For `featured` / `recent`, computes time until 72h after created_at.
 *
 * State buckets:
 *   "live"        — > 12h remaining  (yellow)
 *   "expiring"    — ≤ 12h remaining  (coral, pulse)
 *   "expired"     — past 72h         (grey)
 */
const EXPIRY_HOURS = 72;
const WARN_HOURS = 12;

export function getExpiryInfo(post) {
    if (!post || (post.type !== "featured" && post.type !== "recent")) {
        return null;
    }
    const created = new Date(post.created_at);
    if (Number.isNaN(created.getTime())) return null;

    const cutoff = new Date(created.getTime() + EXPIRY_HOURS * 3600 * 1000);
    const now = new Date();
    const remainingMs = cutoff - now;
    const hours = Math.floor(remainingMs / 3600 / 1000);
    const minutes = Math.floor((remainingMs / 60 / 1000) % 60);

    let state;
    let label;
    if (remainingMs <= 0) {
        state = "expired";
        const overdueH = Math.floor(Math.abs(remainingMs) / 3600 / 1000);
        label = `expired ${overdueH}h ago`;
    } else if (hours < WARN_HOURS) {
        state = "expiring";
        label =
            hours > 0 ? `expires in ${hours}h ${minutes}m` : `expires in ${minutes}m`;
    } else {
        state = "live";
        label = `${hours}h on home`;
    }

    return { state, label, remainingMs };
}

export default function PostExpiryBadge({ post }) {
    const info = getExpiryInfo(post);
    if (!info) return null;

    const palette = {
        live: { bg: "#F5C53D", color: "#1B1B1B" },
        expiring: { bg: "#E63B26", color: "#FFFFFF" },
        expired: { bg: "#A89489", color: "#FFFFFF" },
    }[info.state];

    return (
        <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 text-[0.62rem] font-poppins font-semibold tracking-[0.12em] uppercase ${
                info.state === "expiring" ? "animate-pulse" : ""
            }`}
            style={{ background: palette.bg, color: palette.color }}
            data-testid={`post-expiry-${info.state}`}
            title={info.label}
        >
            {post.type === "featured" ? "Featured" : "Recent"} · {info.label}
        </span>
    );
}
